import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginRequest, MeResponse } from '@app/shared/schemas/auth';
import * as authApi from '../api/auth';
import { accessTokenStore, api, ApiError, refreshSession } from '../api/client';

/**
 * Auth context — bootstraps the access token on app load (silent refresh),
 * exposes `login` / `logout`, and tracks the current user via the `/me`
 * query. The hook is the single source of truth used by route loaders and
 * by the protected layout.
 *
 * State machine:
 *   - `status: 'bootstrapping'` — initial silent refresh in flight
 *   - `status: 'authenticated'` — `user` is the verified profile
 *   - `status: 'anonymous'`     — no session (login required)
 */

export type AuthStatus = 'bootstrapping' | 'authenticated' | 'anonymous';

/**
 * Identity an admin/decision_maker is currently impersonating ("view as
 * partner", ADR-0029). Persisted in `sessionStorage` so it survives an
 * in-tab reload long enough for the bootstrap to reconcile it, but is
 * dropped when the tab closes. The `actor_*` fields capture who started
 * the impersonation so the banner can show "originally signed in as …"
 * and the end-impersonation audit call can attribute correctly.
 */
export interface ImpersonationContext {
  actor_user_id: string;
  actor_email: string;
  target_user_id: string;
  target_email: string;
  target_company_name: string;
}

const IMPERSONATION_STORAGE_KEY = 'tenaedm_impersonation';

function readStoredImpersonation(): ImpersonationContext | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationContext) : null;
  } catch {
    return null;
  }
}

function writeStoredImpersonation(ctx: ImpersonationContext | null): void {
  try {
    if (ctx) sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(ctx));
    else sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private mode / SSR) — impersonation still
    // works for the current page, it just won't survive a reload.
  }
}

export interface AuthContextValue {
  status: AuthStatus;
  user: MeResponse | null;
  login: (input: LoginRequest) => Promise<MeResponse>;
  logout: () => Promise<void>;
  /**
   * Resolves once the initial silent-refresh bootstrap has settled (either
   * to `authenticated` or `anonymous`). Route guards `await` this in their
   * `beforeLoad` so navigation is deterministic — without it the first
   * render can briefly land on a protected page with `status === 'bootstrapping'`
   * and TanStack Router won't re-run `beforeLoad` when the status flips later.
   */
  whenBootstrapped: () => Promise<void>;
  /**
   * Returns the current auth state without relying on a potentially stale
   * React context value captured by an async route guard before bootstrap.
   */
  getSnapshot: () => { status: AuthStatus; user: MeResponse | null };
  /**
   * Non-null while an admin/decision_maker is viewing the app as a partner.
   * Drives the impersonation banner and the "is impersonating" guard logic.
   */
  impersonation: ImpersonationContext | null;
  /**
   * Start impersonation: mint a partner-scoped token, swap it into the
   * in-memory store, and prime `/me` with the partner identity. Does NOT
   * navigate — the caller (which lives under the Router) decides where to go
   * (typically `/portal`). Returns the impersonation context on success.
   */
  impersonate: (input: { userId: string }) => Promise<ImpersonationContext>;
  /**
   * End impersonation: restore the original session via `/auth/refresh`
   * (the caller's untouched cookie) and clear the partner token. Does NOT
   * navigate. Resolves once the original `/me` has been re-primed.
   */
  endImpersonation: () => Promise<void>;
  /**
   * Transient, self-clearing notice emitted when impersonation ends —
   * including the implicit end caused by a page reload. The banner surfaces
   * it briefly; `null` most of the time.
   */
  impersonationNotice: string | null;
  /** Dismiss the current impersonation notice. */
  clearImpersonationNotice: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Exported for tests that need to inject a custom auth context value. */
export { AuthContext };

interface AuthProviderProps {
  children: ReactNode | ((auth: AuthContextValue) => ReactNode);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [impersonation, setImpersonation] = useState<ImpersonationContext | null>(null);
  const [impersonationNotice, setImpersonationNotice] = useState<string | null>(null);
  const statusRef = useRef<AuthStatus>('bootstrapping');
  const setAuthStatus = useCallback((nextStatus: AuthStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  // Bootstrap promise — created once per provider instance. Route guards
  // `await` it in `beforeLoad` so they see a settled status. Resolving
  // happens exactly once on the first `bootstrapping → authenticated|anonymous`
  // transition (see `setStatus` callers below).
  const bootstrapPromiseRef = useRef<Promise<void> | null>(null);
  const bootstrapResolveRef = useRef<(() => void) | null>(null);
  if (bootstrapPromiseRef.current === null) {
    bootstrapPromiseRef.current = new Promise<void>((resolve) => {
      bootstrapResolveRef.current = resolve;
    });
  }
  const resolveBootstrap = useCallback(() => {
    if (bootstrapResolveRef.current) {
      bootstrapResolveRef.current();
      bootstrapResolveRef.current = null;
    }
  }, []);

  // Bootstrap: silently refresh, then verify and cache the current profile before guards run.
  useEffect(() => {
    // Reconcile a stale impersonation context left in sessionStorage by a
    // reload. The impersonation token is access-only (never refreshable), so
    // the refresh below restores the ORIGINAL user — impersonation is over.
    // Clear the marker and surface a one-line notice so the admin isn't
    // confused about whose session they're now in.
    const stored = readStoredImpersonation();
    if (stored) {
      writeStoredImpersonation(null);
      setImpersonationNotice(
        `Zobrazení jako ${stored.target_email} bylo ukončeno obnovením stránky.`,
      );
    }

    let cancelled = false;
    (async () => {
      try {
        // Go through the shared refresh channel so a bootstrap refresh and an
        // interceptor-triggered one (e.g. an eager query that 401s during
        // boot) coalesce into a single `/auth/refresh` — two concurrent
        // refreshes would rotate the token twice and could trip server-side
        // reuse detection.
        await refreshSession();
        try {
          const user = await queryClient.fetchQuery({
            queryKey: ['auth', 'me'],
            queryFn: () => authApi.me(),
            staleTime: 60_000,
          });
          queryClient.setQueryData(['auth', 'me'], user);
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) throw error;
        }
        if (!cancelled) setAuthStatus('authenticated');
      } catch {
        if (!cancelled) setAuthStatus('anonymous');
      } finally {
        // Always resolve — guards waiting in `beforeLoad` must unblock even
        // if the refresh threw, so they can apply the anonymous branch.
        if (!cancelled) resolveBootstrap();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [queryClient, resolveBootstrap, setAuthStatus]);

  // Wire the API client's session-expiry callback so global 401-after-refresh
  // failures flip us back to anonymous and clear the cached `/me` data.
  useEffect(() => {
    const expirable = api as typeof api & { onSessionExpired?: (cb: () => void) => void };
    expirable.onSessionExpired?.(() => {
      accessTokenStore.set(null);
      queryClient.removeQueries({ queryKey: ['auth', 'me'] });
      setAuthStatus('anonymous');
    });
  }, [queryClient, setAuthStatus]);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });

  // If the /me query terminally fails with 401, drop back to anonymous.
  useEffect(() => {
    if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
      accessTokenStore.set(null);
      setAuthStatus('anonymous');
    }
  }, [meQuery.error, setAuthStatus]);

  const login = useCallback(
    async (input: LoginRequest) => {
      const result = await authApi.login(input);
      accessTokenStore.set(result.access_token);
      queryClient.setQueryData(['auth', 'me'], result.user);
      setAuthStatus('authenticated');
      return result.user;
    },
    [queryClient, setAuthStatus],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Logout is best-effort — even if the server call fails we still drop the local state.
    }
    accessTokenStore.set(null);
    writeStoredImpersonation(null);
    setImpersonation(null);
    queryClient.clear();
    setAuthStatus('anonymous');
  }, [queryClient, setAuthStatus]);

  const impersonate = useCallback(
    async ({ userId }: { userId: string }): Promise<ImpersonationContext> => {
      // Capture the original actor BEFORE swapping the token — once the
      // partner token is in place, `/me` returns the partner, not the admin.
      const actor = queryClient.getQueryData<MeResponse>(['auth', 'me']);
      if (!actor) {
        throw new Error('Cannot impersonate without an authenticated session');
      }

      const result = await authApi.impersonate({ user_id: userId });

      const ctx: ImpersonationContext = {
        actor_user_id: actor.id,
        actor_email: actor.email,
        target_user_id: result.target_user.id,
        target_email: result.target_user.email,
        target_company_name: result.target_user.company_name,
      };

      accessTokenStore.set(result.access_token);
      writeStoredImpersonation(ctx);
      setImpersonation(ctx);
      setImpersonationNotice(null);
      // Drop any cached admin-scoped data, then prime `/me` with the partner
      // identity so guards + UI react immediately under the partner scope.
      queryClient.removeQueries({ queryKey: ['auth', 'me'], exact: true });
      queryClient.setQueryData<MeResponse>(['auth', 'me'], {
        id: result.target_user.id,
        email: result.target_user.email,
        role: result.target_user.role,
        partner_id: result.target_user.partner_id,
        // Permissions aren't returned by /impersonate; the next /me refetch
        // hydrates them. Portal UI is role-gated, not permission-gated.
        permissions: [],
      });
      return ctx;
    },
    [queryClient],
  );

  const endImpersonation = useCallback(async () => {
    const ctx = readStoredImpersonation();
    // Best-effort audit close while we still know the original actor id.
    if (ctx) {
      try {
        await authApi.endImpersonation({ actor_user_id: ctx.actor_user_id });
      } catch {
        // Non-fatal — the session restore below is what actually matters.
      }
    }
    writeStoredImpersonation(null);
    setImpersonation(null);
    // Restore the original session: the admin's refresh cookie was never
    // touched, so /auth/refresh mints a fresh admin access token. Go through
    // the shared refresh channel to stay de-duplicated with the interceptor.
    try {
      await refreshSession();
      setAuthStatus('authenticated');
    } catch {
      accessTokenStore.set(null);
      setAuthStatus('anonymous');
    }
    // Force /me to refetch the original identity.
    queryClient.removeQueries({ queryKey: ['auth', 'me'], exact: true });
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient, setAuthStatus]);

  const clearImpersonationNotice = useCallback(() => setImpersonationNotice(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: status === 'authenticated' ? (meQuery.data ?? null) : null,
      login,
      logout,
      whenBootstrapped: () => bootstrapPromiseRef.current ?? Promise.resolve(),
      getSnapshot: () => ({
        status: statusRef.current,
        user: queryClient.getQueryData<MeResponse>(['auth', 'me']) ?? null,
      }),
      impersonation,
      impersonate,
      endImpersonation,
      impersonationNotice,
      clearImpersonationNotice,
    }),
    [
      status,
      meQuery.data,
      login,
      logout,
      queryClient,
      impersonation,
      impersonate,
      endImpersonation,
      impersonationNotice,
      clearImpersonationNotice,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
